import { call, put, takeLatest } from 'redux-saga/effects';
import * as action from 'actions';
import * as actNotif from 'actions/notifications';
import _ from 'lodash';
import moment from 'moment';
import {
  setSubscribeBasicButton,
  setSubscribeProButton,
  featurePrice,
  featurePricePerMonths,
  setPageOnLoading,
  getBillings,
  setFeaturePrice,
  setFeaturePricePerMonths,
  setPendingPaymentMessage,
  setSubscriptionDetail,
  setCurrentOutletCount,
  setPendingOutletMessage,
  showPaymentGatewayErrorModal,
  subscriptionStatus,
} from 'actions/customers/loyalty/loyaltySubscription';
import { getFeaturePricePerMonths } from 'api/feature/getFeaturePricePerMonths';
import { getFeaturePrice } from 'api/feature/getFeaturePrice';
import { getSubscriptionStatus } from 'api/feature/getSubscriptionStatus';
import { startTrial } from 'api/feature/startTrial';
import { endTutorial } from 'api/feature/endTutorial';
import * as apiBilling from 'api/billing';
import * as apiLoyalty from 'api/customers/loyalty';
import {
  LOYALTY_SUBSCRIPTION_LOAD,
  LOYALTY_SUBSCRIPTION_PAGE_REQUEST,
  LOYALTY_SUBSCRIPTION_FEATURE_PRICE,
  LOYALTY_SUBSCRIPTION_FEATURE_PRICE_PER_MONTHS,
  LOYALTY_SUBSCRIPTION_GET_BILLINGS,
  LOYALTY_SUBSCRIPTION_SUBSCRIPTION_STATUS,
  LOYALTY_SUBSCRIPTION_SUBSCRIBE_BASIC,
  LOYALTY_SUBSCRIPTION_SUBSCRIBE_PRO,
  LOYALTY_SUBSCRIPTION_START_PRO_TRIAL,
} from 'actions/customers/loyalty/loyaltySubscription/constant';
import { showBillingNotification } from 'actions/billingNotification/billingNotification';
import {
  setNotificationMessage,
  setNotificationType,
} from 'actions/billingRedirect/index';
import * as is from 'is_js';

const featureNames = {
  loyalty: 'Loyalty',
  loyalty_pro: 'Loyalty Pro',
};

export function* loadLoyaltySubscriptionsFlow(request) {
  const { billingNotification } = request.payload;
  yield put(setPageOnLoading(true));
  yield put(getBillings());
  yield put(subscriptionStatus(Object.values(featureNames)));
  yield put(showBillingNotification(billingNotification));
  yield put(setNotificationType(null));
  yield put(setNotificationMessage(null));
  yield put(setPageOnLoading(false));
}

export function* subscriptionPageRequestFlow(request) {
  const { trialEndAt, outletSlot, alreadySubscribed } = request.payload;
  yield put(setPageOnLoading(true));
  yield put(featurePrice(trialEndAt, outletSlot, alreadySubscribed));
  yield put(featurePricePerMonths(Object.values(featureNames)));
  yield put(setPageOnLoading(false));
}

export function* getBillingsFlow() {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  const response = yield call(apiBilling.getBillings);
  let pendingPaymentMessage = null;
  _.forEach(response.data, invoice => {
    if (invoice.status === 'pending') {
      if (invoice.payment_type === 'credit_card') {
        pendingPaymentMessage = `There is a pending payment for ${invoice.invoice_number}. Please wait 24 hours before making a new payment.`;
      } else if (
        invoice.payment_type === 'bank_transfer' ||
        invoice.payment_type === 'echannel'
      ) {
        pendingPaymentMessage = `There is a pending payment for ${invoice.invoice_number}. Please transfer full amount of the invoice within 3 days before making a new payment.`;
      }
      return false;
    }
  });
  if (pendingPaymentMessage !== null) {
    yield put(setPendingPaymentMessage(pendingPaymentMessage));
    yield put(setSubscribeProButton(false));
  }
  yield put(action.sendingRequest(false));
  yield put(action.hideProgressBar());
}

export function* getFeaturePricePerMonthsFlow(request) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  const response = yield call(getFeaturePricePerMonths, {
    feature_names: request.payload,
  });
  yield put(setFeaturePricePerMonths(response.data));
  yield put(action.sendingRequest(false));
  yield put(action.hideProgressBar());
}

export function* getFeaturePriceFlow(request) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  const { trialEndAt, outletSlot, alreadySubscribed } = request.payload;
  const response = yield call(getFeaturePrice, {
    feature_name: featureNames.loyalty_pro,
  });
  const subscriptionPriceDetail = {};
  let currentOutlet = 0;
  const today = new Date().getTime();
  const trialEndDate = new Date(
    moment(trialEndAt).format('YYYY-MM-DD')
  ).getTime();

  _.map(response.data, (value, key) => {
    if (key !== 'final_price' && value.final_price !== 0) {
      currentOutlet++;
      subscriptionPriceDetail[key] = {};
      subscriptionPriceDetail[key].start_date =
        today < trialEndDate ? new Date() : value.start_date;
      subscriptionPriceDetail[key].final_price = value.final_price;
      subscriptionPriceDetail[key].end_date = value.end_date;
    } else {
      subscriptionPriceDetail[key] = value;
    }
  });
  const outletLeft = !alreadySubscribed ? 0 : outletSlot - currentOutlet;
  yield put(setCurrentOutletCount(currentOutlet));
  if (outletLeft > 0) {
    yield put(
      setPendingOutletMessage(
        `You still have slots for additional ${outletLeft} ${
          outletLeft === 1 ? 'outlet' : 'outlets'
        }. Please create the outlets before making a payment.`
      )
    );
    yield put(setSubscribeProButton(false));
  }
  yield put(setFeaturePrice(featureNames.loyalty_pro, subscriptionPriceDetail));
  yield put(action.sendingRequest(false));
  yield put(action.hideProgressBar());
}

export function* getSubscriptionStatusFlow(request) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  const response = yield call(getSubscriptionStatus, {
    feature_names: request.payload,
  });
  yield put(setSubscriptionDetail(response.data));
  yield put(action.sendingRequest(false));
  yield put(action.hideProgressBar());
}

export function* subscribeBasicFlow() {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());
  yield put(setSubscribeBasicButton(false));
  yield put(setPageOnLoading(true));
  try {
    yield call(apiLoyalty.addSubscription);
    const response = yield call(endTutorial, {
      feature_name: featureNames.loyalty,
    });
    yield put(setSubscriptionDetail(response.data));
  } catch (error) {
    yield put(setSubscribeBasicButton(true));
    yield put(actNotif.error('Please Try Again'));
  } finally {
    yield put(action.sendingRequest(false));
    yield put(action.hideProgressBar());
    yield put(setPageOnLoading(false));
  }
}

export function* subscribeProFlow(request) {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());
  yield put(setSubscribeProButton(false));
  yield put(setPageOnLoading(true));

  let response = {};

  try {
    response = yield call(
      apiBilling.verifyBilling,
      request.payload.billingData
    );
  } catch (error) {
    yield put(setSubscribeProButton(true));
    if (!error.data || error.data === ' ' || error.status >= 500) {
      yield put(showPaymentGatewayErrorModal());
    } else {
      yield put(actNotif.error(error.data.message));
    }
  } finally {
    yield put(action.sendingRequest(false));
    yield put(action.hideProgressBar());
    yield put(setPageOnLoading(false));
    if (response.data) {
      if (is.safari()) {
        yield put(setSubscribeProButton(true));
      }
      window.location.href = response.data.data.redirect_url;
    }
  }
}

export function* startProTrialFlow() {
  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());
  yield put(setPageOnLoading(true));

  try {
    yield call(startTrial, { feature_name: featureNames.loyalty_pro });
    yield call(endTutorial, { feature_name: featureNames.loyalty_pro });
    yield put(subscriptionStatus(Object.values(featureNames)));
    yield put(actNotif.success('CRM Pro feature is unlocked.'));
  } catch (error) {
    yield put(actNotif.error('Please try again later.'));
  } finally {
    yield put(action.sendingRequest(false));
    yield put(action.hideProgressBar());
    yield put(setPageOnLoading(false));
  }
}

export default function* saga() {
  yield takeLatest(LOYALTY_SUBSCRIPTION_LOAD, loadLoyaltySubscriptionsFlow);
  yield takeLatest(
    LOYALTY_SUBSCRIPTION_PAGE_REQUEST,
    subscriptionPageRequestFlow
  );
  yield takeLatest(
    LOYALTY_SUBSCRIPTION_FEATURE_PRICE_PER_MONTHS,
    getFeaturePricePerMonthsFlow
  );
  yield takeLatest(LOYALTY_SUBSCRIPTION_FEATURE_PRICE, getFeaturePriceFlow);
  yield takeLatest(LOYALTY_SUBSCRIPTION_GET_BILLINGS, getBillingsFlow);
  yield takeLatest(
    LOYALTY_SUBSCRIPTION_SUBSCRIPTION_STATUS,
    getSubscriptionStatusFlow
  );
  yield takeLatest(LOYALTY_SUBSCRIPTION_SUBSCRIBE_BASIC, subscribeBasicFlow);
  yield takeLatest(LOYALTY_SUBSCRIPTION_SUBSCRIBE_PRO, subscribeProFlow);
  yield takeLatest(LOYALTY_SUBSCRIPTION_START_PRO_TRIAL, startProTrialFlow);
}
